import { useMemo } from 'react';

import { OPEN_LOAD_STATUSES } from './kioskConstants';
import {
  buildDisplayedRequestLoadName,
  buildInboundRequestLoadPrefix,
  canCreateNextRequest,
  deriveRequestLoadStatus,
  getRegisterOperationBlocker,
  sortRequestLoads,
} from './kioskTransforms';

export function useKioskLoadModel({
  createForm,
  currentRequest,
  currentRequestLoad,
  deviceMap,
  requestLoadForm,
  requestLoads,
  requests,
  selectedBinding,
  selectedRegisterId,
  selectedRequestLoadId,
}) {
  const currentRequestForSelectedRegister = currentRequest?.registerId === selectedRegisterId
    ? currentRequest
    : null;
  const selectedCreateRequestLoad = useMemo(
    () => requestLoads.find((requestLoad) => requestLoad.loadId === createForm.requestLoadId) ?? null,
    [createForm.requestLoadId, requestLoads],
  );
  const selectedRequestLoad = useMemo(() => {
    if (!selectedRequestLoadId) {
      return null;
    }

    return requestLoads.find((requestLoad) => requestLoad.loadId === selectedRequestLoadId)
      ?? (currentRequestLoad?.loadId === selectedRequestLoadId ? currentRequestLoad : null);
  }, [currentRequestLoad, requestLoads, selectedRequestLoadId]);
  const assignedRequestLoad = currentRequestForSelectedRegister?.requestLoadId
    ? requestLoads.find((requestLoad) => (
        requestLoad.loadId === currentRequestForSelectedRegister.requestLoadId
      ))
      ?? (currentRequestLoad?.loadId === currentRequestForSelectedRegister.requestLoadId
        ? currentRequestLoad
        : null)
    : null;
  const canCreateFromCurrentRequest = canCreateNextRequest(currentRequestForSelectedRegister);
  const activeCreateRequestLoad = selectedCreateRequestLoad
    ?? (canCreateFromCurrentRequest ? currentRequestLoad : null);
  const displayedRequestLoad = currentRequestForSelectedRegister && !canCreateFromCurrentRequest
    ? (selectedRequestLoad
      ?? assignedRequestLoad)
    : activeCreateRequestLoad;
  const displayedRequestLoadRequests = useMemo(() => {
    if (!displayedRequestLoad?.loadId) {
      return [];
    }

    return requests.filter((request) => (
      request.requestLoadId === displayedRequestLoad.loadId
      && (!displayedRequestLoad.registerId || request.registerId === displayedRequestLoad.registerId)
    ));
  }, [displayedRequestLoad?.loadId, displayedRequestLoad?.registerId, requests]);
  const recentOpenRequestLoads = useMemo(() => {
    if (!selectedRegisterId) {
      return [];
    }

    return sortRequestLoads(requestLoads.filter((requestLoad) => {
      if (!OPEN_LOAD_STATUSES.has(deriveRequestLoadStatus(requestLoad))) {
        return false;
      }

      return requestLoad.registerId === selectedRegisterId
        || !requestLoad.registerId
        || requests.some((request) => (
          request.requestLoadId === requestLoad.loadId
          && request.registerId === selectedRegisterId
        ));
    }));
  }, [requestLoads, requests, selectedRegisterId]);
  const openRequestLoads = useMemo(
    () => sortRequestLoads(requestLoads.filter((requestLoad) => (
      OPEN_LOAD_STATUSES.has(deriveRequestLoadStatus(requestLoad))
    ))).slice(0, 50),
    [requestLoads],
  );
  const registerOperationBlocker = useMemo(
    () => getRegisterOperationBlocker(selectedBinding, deviceMap),
    [deviceMap, selectedBinding],
  );
  const outboundRequestLoads = useMemo(
    () => requestLoads.filter((requestLoad) => requestLoad.direction === 'Outbound'),
    [requestLoads],
  );
  const matchedOutboundLoadIds = useMemo(
    () => new Set(
      requestLoads
        .filter((requestLoad) => (
          requestLoad.direction === 'Inbound'
          && typeof requestLoad.linkedOutboundLoadId === 'string'
          && requestLoad.linkedOutboundLoadId.trim()
        ))
        .map((requestLoad) => requestLoad.linkedOutboundLoadId.trim()),
    ),
    [requestLoads],
  );
  const availableOutboundRequestLoads = useMemo(
    () => outboundRequestLoads.filter((requestLoad) => !matchedOutboundLoadIds.has(requestLoad.loadId)),
    [matchedOutboundLoadIds, outboundRequestLoads],
  );
  const linkedOutboundRequestLoad = useMemo(
    () => outboundRequestLoads.find((requestLoad) => (
      requestLoad.loadId === requestLoadForm.linkedOutboundLoadId
    )) ?? null,
    [outboundRequestLoads, requestLoadForm.linkedOutboundLoadId],
  );
  const inboundRequestLoadPrefix = useMemo(
    () => buildInboundRequestLoadPrefix(linkedOutboundRequestLoad),
    [linkedOutboundRequestLoad],
  );
  const displayedRequestLoadName = useMemo(
    () => buildDisplayedRequestLoadName(requestLoadForm, linkedOutboundRequestLoad),
    [linkedOutboundRequestLoad, requestLoadForm],
  );

  return {
    availableOutboundRequestLoads,
    canCreateFromCurrentRequest,
    currentRequestForSelectedRegister,
    displayedRequestLoad,
    displayedRequestLoadName,
    displayedRequestLoadRequests,
    inboundRequestLoadPrefix,
    openRequestLoads,
    recentOpenRequestLoads,
    registerOperationBlocker,
    selectedRequestLoad,
  };
}
